import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { Dialog } from 'primeng/dialog';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of, forkJoin } from 'rxjs';
import {
  NotificationSetting,
  NotificationGroupOption,
  ServiceGroupRecipient,
  MASTER_SWITCH_TYPE,
  CONTRACT_NOTIFICATION_TYPES
} from '../../models/notification-setting.model';
import { NotificationSettingService } from '../../services/notification-setting.service';
import { Employee } from '../../models/employee.model';
import { EmployeeService } from '../../services/employee.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

@Component({
  selector: 'app-notification-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Select,
    MultiSelect,
    InputText,
    Dialog,
    ToggleSwitch,
    Tooltip,
    Tag,
    ConfirmDialog
  ],
  templateUrl: './notification-settings.component.html',
  styleUrl: './notification-settings.component.scss',
  providers: [ConfirmationService]
})
export class NotificationSettingsComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  /** All rows except the master switch, which is rendered separately above the table. */
  settings: NotificationSetting[] = [];
  filteredSettings: NotificationSetting[] = [];
  masterSwitch: NotificationSetting | null = null;

  employees: Employee[] = [];
  /** Employee emails picked per setting id. */
  selectedRecipients: Record<string, string[]> = {};
  /** Hand-typed addresses per setting id. */
  otherRecipients: Record<string, string[]> = {};
  /** Rows currently being saved, so the UI can disable them mid-request. */
  savingIds = new Set<string>();

  groupOptions: NotificationGroupOption[] = [];

  /** Per-service-group contract recipients, edited inline under the contract notifications. */
  serviceGroups: ServiceGroupRecipient[] = [];
  serviceGroupSelections: Record<string, string[]> = {};
  serviceGroupsExpanded = false;
  savingServiceGroupIds = new Set<string>();

  /** Row open in the edit dialog, plus its working copy of the three recipient sources. */
  editingRow: NotificationSetting | null = null;
  editDialogVisible = false;
  editEmployees: string[] = [];
  editOther: string[] = [];
  editGroupKey: string | null = null;
  editOtherInput = '';

  loading = false;
  searchText = '';

  /** Categories the admin has collapsed. */
  collapsedCategories = new Set<string>();

  /** Guards the collapse-on-load default so it applies once, not on every post-save refresh. */
  private initialCollapseApplied = false;

  selectedStatusFilter: string | null = null;
  selectedCategoryFilter: string | null = null;
  selectedRecipientTypeFilter: string | null = null;

  statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'Enabled', value: 'enabled' },
    { label: 'Disabled', value: 'disabled' },
    { label: 'Will not send (no recipients)', value: 'norecipients' }
  ];

  recipientTypeFilterOptions = [
    { label: 'All', value: null },
    { label: 'Recipients I set', value: 'configurable' },
    { label: 'Automatic recipients', value: 'automatic' }
  ];

  get totalCount(): number { return this.settings.length; }
  get enabledCount(): number { return this.settings.filter(s => s.isEnabled).length; }
  get disabledCount(): number { return this.settings.filter(s => !s.isEnabled).length; }
  get configurableCount(): number { return this.settings.filter(s => s.areRecipientsEditable).length; }
  get isMasterEnabled(): boolean { return this.masterSwitch?.isEnabled !== false; }

  /**
   * Enabled notifications that would send to nobody. Since recipients no longer fall back to
   * configuration, these are silently broken and worth surfacing prominently.
   */
  get willNotSendCount(): number {
    return this.settings.filter(s => this.willNotSend(s)).length;
  }

  /** Category options for the filter dropdown, derived from the loaded rows. */
  get categoryFilterOptions(): { label: string; value: string | null }[] {
    const names = [...new Set(this.settings.map(s => s.category))];
    return [{ label: 'All categories', value: null }, ...names.map(n => ({ label: n, value: n }))];
  }

  constructor(
    private notificationSettingService: NotificationSettingService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search notifications by name or recipient...');
    this.searchText = this.pageHeaderService.currentSearchText;
    this.load();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      settings: this.notificationSettingService.getAll().pipe(
        catchError(error => {
          console.error('Error loading notification settings:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load notification settings.'
          });
          return of([] as NotificationSetting[]);
        })
      ),
      // getAllEmployees (not /names) — the lightweight endpoint carries no email address.
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          console.error('Error loading employees:', error);
          this.messageService.add({
            severity: 'warn',
            summary: 'Employees unavailable',
            detail: 'Could not load the employee list. Recipients cannot be selected.'
          });
          return of([] as Employee[]);
        })
      ),
      groups: this.notificationSettingService.getGroupOptions().pipe(
        catchError(() => of([] as NotificationGroupOption[]))
      ),
      serviceGroups: this.notificationSettingService.getServiceGroupRecipients().pipe(
        catchError(() => of([] as ServiceGroupRecipient[]))
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ settings, employees, groups, serviceGroups }) => {
        this.employees = (employees ?? [])
          .filter(e => e.isActive && !!e.email?.trim())
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        this.groupOptions = groups ?? [];

        this.serviceGroups = serviceGroups ?? [];
        this.serviceGroupSelections = {};
        for (const sg of this.serviceGroups) {
          this.serviceGroupSelections[sg.id] = this.splitEmails(sg.notificationEmails);
        }

        const rows = settings ?? [];
        this.masterSwitch = rows.find(s => s.notificationType === MASTER_SWITCH_TYPE) ?? null;
        this.settings = rows.filter(s => s.notificationType !== MASTER_SWITCH_TYPE);

        this.selectedRecipients = {};
        this.otherRecipients = {};
        for (const row of this.settings) {
          this.selectedRecipients[row.id] = this.splitEmails(row.recipients);
          this.otherRecipients[row.id] = this.splitEmails(row.otherRecipients);
        }

        this.applySearch();

        // Start collapsed: 38 notifications in one flow is a long scroll, and each header already
        // shows an "N of M enabled" count, so the collapsed view is scannable on its own. Only on
        // the first load — a refresh after saving must not fold up the section being worked on.
        if (!this.initialCollapseApplied) {
          this.collapsedCategories = new Set(this.categories);
          this.initialCollapseApplied = true;
        }

        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  applySearch(): void {
    const term = this.searchText.trim().toLowerCase();

    this.filteredSettings = this.settings.filter(s => {
      if (term
        && !s.displayName?.toLowerCase().includes(term)
        && !s.category?.toLowerCase().includes(term)
        && !s.recipients?.toLowerCase().includes(term)
        && !s.description?.toLowerCase().includes(term)) {
        return false;
      }

      if (this.selectedCategoryFilter && s.category !== this.selectedCategoryFilter) {
        return false;
      }

      if (this.selectedStatusFilter === 'enabled' && !s.isEnabled) return false;
      if (this.selectedStatusFilter === 'disabled' && s.isEnabled) return false;
      if (this.selectedStatusFilter === 'norecipients' && !this.willNotSend(s)) return false;

      if (this.selectedRecipientTypeFilter === 'configurable' && !s.areRecipientsEditable) return false;
      if (this.selectedRecipientTypeFilter === 'automatic' && s.areRecipientsEditable) return false;

      return true;
    });

    // Anything the user narrowed to must be visible. With sections collapsed by default, a search
    // that matches three rows would otherwise look like it returned nothing.
    if (this.hasActiveFilter) {
      this.collapsedCategories.clear();
    }
  }

  /** True when any search term or filter is narrowing the list. */
  private get hasActiveFilter(): boolean {
    return !!this.searchText.trim()
      || !!this.selectedCategoryFilter
      || !!this.selectedStatusFilter
      || !!this.selectedRecipientTypeFilter;
  }

  onFilterChange(): void {
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim()
      || this.selectedStatusFilter
      || this.selectedCategoryFilter
      || this.selectedRecipientTypeFilter);
  }

  clearFilters(): void {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = null;
    this.selectedCategoryFilter = null;
    this.selectedRecipientTypeFilter = null;
    this.applySearch();
  }

  /** Jump straight to the notifications that are enabled but have nobody to send to. */
  showOnlyWillNotSend(): void {
    this.selectedStatusFilter = 'norecipients';
    this.selectedCategoryFilter = null;
    this.selectedRecipientTypeFilter = null;
    this.collapsedCategories.clear();
    this.applySearch();
  }

  /**
   * True when an enabled notification has no recipients at all and would send nothing.
   * Only applies to rows whose recipients are configured here — rows with an Entra group or
   * record-derived recipients get theirs at send time.
   */
  willNotSend(row: NotificationSetting): boolean {
    return row.isEnabled
      && row.areRecipientsEditable
      && !row.adGroupConfigKey
      && this.allRecipients(row).length === 0;
  }

  /** Distinct categories in display order, for section rendering. */
  get categories(): string[] {
    return [...new Set(this.filteredSettings.map(s => s.category))];
  }

  settingsIn(category: string): NotificationSetting[] {
    return this.filteredSettings.filter(s => s.category === category);
  }

  enabledIn(category: string): number {
    return this.settingsIn(category).filter(s => s.isEnabled).length;
  }

  isCollapsed(category: string): boolean {
    return this.collapsedCategories.has(category);
  }

  toggleCategory(category: string): void {
    if (!this.collapsedCategories.delete(category)) {
      this.collapsedCategories.add(category);
    }
  }

  collapseAll(): void {
    this.collapsedCategories = new Set(this.categories);
  }

  expandAll(): void {
    this.collapsedCategories.clear();
  }

  get allCollapsed(): boolean {
    return this.categories.length > 0 && this.categories.every(c => this.isCollapsed(c));
  }

  /** Employee display name for an address, so chips read as people rather than raw emails. */
  recipientLabel(email: string): string {
    const match = this.employees.find(
      e => e.email?.trim().toLowerCase() === email.trim().toLowerCase());
    return match?.name ?? email;
  }

  isKnownEmployee(email: string): boolean {
    return this.employees.some(
      e => e.email?.trim().toLowerCase() === email.trim().toLowerCase());
  }

  private static readonly EmailPattern = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

  onToggle(row: NotificationSetting): void {
    this.persist(row);
  }

  onRecipientsChanged(row: NotificationSetting): void {
    this.persist(row);
  }

  // ---- edit dialog: group + employees + other addresses ----

  openEdit(row: NotificationSetting): void {
    this.editingRow = row;
    this.editEmployees = [...(this.selectedRecipients[row.id] ?? [])];
    this.editOther = [...(this.otherRecipients[row.id] ?? [])];
    this.editGroupKey = row.adGroupConfigKey ?? null;
    this.editOtherInput = '';
    this.editDialogVisible = true;
  }

  closeEdit(): void {
    this.editDialogVisible = false;
    this.editingRow = null;
    this.editOtherInput = '';
  }

  /** Adds whatever is typed in the "other addresses" box, validating it first. */
  addOtherEmail(): void {
    const value = this.editOtherInput.trim();
    if (!value) return;

    if (!NotificationSettingsComponent.EmailPattern.test(value)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid email address',
        detail: `"${value}" is not a valid email address`,
        life: 3500
      });
      return;
    }

    const alreadyThere = [...this.editOther, ...this.editEmployees]
      .some(e => e.toLowerCase() === value.toLowerCase());
    if (alreadyThere) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Already added',
        detail: `${value} is already a recipient`,
        life: 3000
      });
      return;
    }

    this.editOther = [...this.editOther, value];
    this.editOtherInput = '';
  }

  removeOtherEmail(email: string): void {
    this.editOther = this.editOther.filter(e => e !== email);
  }

  /** Employee options for the dialog's multi-select, labelled with name and address. */
  get employeeOptions(): { label: string; value: string }[] {
    return this.employees.map(e => ({
      label: `${e.name} (${e.email!.trim()})`,
      value: e.email!.trim()
    }));
  }

  /** Group options plus a "no group" entry, with unconfigured groups marked. */
  get groupSelectOptions(): { label: string; value: string | null; disabled?: boolean }[] {
    return [
      { label: 'No group', value: null },
      ...this.groupOptions.map(g => ({
        label: g.isConfigured ? g.name : `${g.name} (not configured here)`,
        value: g.configKey,
        disabled: !g.isConfigured
      }))
    ];
  }

  /** True when the dialog's three sources would leave the notification with nobody to email. */
  get editHasNoRecipients(): boolean {
    return !this.editGroupKey && this.editEmployees.length === 0 && this.editOther.length === 0;
  }

  /**
   * Plain-language summary of the three fields, so the dialog states the outcome rather than
   * leaving the admin to work it out from three separate controls.
   */
  get editSummary(): string {
    const parts: string[] = [];

    if (this.editGroupKey) {
      const group = this.groupOptions.find(g => g.configKey === this.editGroupKey);
      parts.push(`everyone in ${group?.name ?? 'the selected team'}`);
    }

    if (this.editEmployees.length === 1) {
      parts.push(this.recipientLabel(this.editEmployees[0]));
    } else if (this.editEmployees.length > 1) {
      parts.push(`${this.editEmployees.length} people`);
    }

    if (this.editOther.length === 1) {
      parts.push(this.editOther[0]);
    } else if (this.editOther.length > 1) {
      parts.push(`${this.editOther.length} other addresses`);
    }

    if (parts.length <= 1) return parts[0] ?? '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  }

  saveEdit(): void {
    const row = this.editingRow;
    if (!row) return;

    this.selectedRecipients[row.id] = [...this.editEmployees];
    this.otherRecipients[row.id] = [...this.editOther];
    row.adGroupConfigKey = this.editGroupKey ?? undefined;

    this.persist(row);
    this.closeEdit();
  }

  // ---- per-service-group contract recipients ----

  /**
   * True for the four contract expiry notifications, which additionally route to the heads of
   * each contract's own service group.
   */
  isContractNotification(row: NotificationSetting): boolean {
    return CONTRACT_NOTIFICATION_TYPES.includes(row.notificationType);
  }

  /** Shown once, under the first contract notification in the list. */
  isFirstContractNotification(row: NotificationSetting): boolean {
    const contractRows = this.filteredSettings.filter(s => this.isContractNotification(s));
    return contractRows.length > 0 && contractRows[0].id === row.id;
  }

  get serviceGroupsWithRecipients(): number {
    return this.serviceGroups.filter(sg => (this.serviceGroupSelections[sg.id] ?? []).length > 0).length;
  }

  toggleServiceGroups(): void {
    this.serviceGroupsExpanded = !this.serviceGroupsExpanded;
  }

  isSavingServiceGroup(sg: ServiceGroupRecipient): boolean {
    return this.savingServiceGroupIds.has(sg.id);
  }

  saveServiceGroup(sg: ServiceGroupRecipient): void {
    const emails = (this.serviceGroupSelections[sg.id] ?? []).join('; ');
    this.savingServiceGroupIds.add(sg.id);

    this.notificationSettingService.updateServiceGroupRecipients(sg.id, emails)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updated) => {
          sg.notificationEmails = updated.notificationEmails;
          this.savingServiceGroupIds.delete(sg.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: `${sg.name} recipients updated`,
            life: 2000
          });
        },
        error: (error) => {
          console.error('Error updating service group recipients:', error);
          this.savingServiceGroupIds.delete(sg.id);
          this.serviceGroupSelections[sg.id] = this.splitEmails(sg.notificationEmails);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to update ${sg.name}`
          });
        }
      });
  }

  /** Display name of the group a row sends to, for the row summary. */
  groupNameFor(row: NotificationSetting): string | null {
    if (!row.adGroupConfigKey) return null;
    return this.groupOptions.find(g => g.configKey === row.adGroupConfigKey)?.name
      ?? row.adGroupConfigKey;
  }

  /** All recipients of a row, across the three sources, for the row summary. */
  allRecipients(row: NotificationSetting): string[] {
    return [
      ...(this.selectedRecipients[row.id] ?? []),
      ...(this.otherRecipients[row.id] ?? [])
    ];
  }

  private persist(row: NotificationSetting): void {
    this.savingIds.add(row.id);

    this.notificationSettingService.update(row.id, {
      isEnabled: row.isEnabled,
      recipients: row.areRecipientsEditable
        ? (this.selectedRecipients[row.id] ?? []).join('; ')
        : undefined,
      otherRecipients: row.areRecipientsEditable
        ? (this.otherRecipients[row.id] ?? []).join('; ')
        : undefined,
      adGroupConfigKey: row.areRecipientsEditable ? (row.adGroupConfigKey ?? null) : undefined
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        row.recipients = updated.recipients;
        row.otherRecipients = updated.otherRecipients;
        row.adGroupConfigKey = updated.adGroupConfigKey;
        this.savingIds.delete(row.id);
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: `${row.displayName} updated`,
          life: 2000
        });
      },
      error: (error) => {
        console.error('Error updating notification setting:', error);
        this.savingIds.delete(row.id);
        // Put the UI back to the server's last known state rather than leaving a lie on screen.
        row.isEnabled = !row.isEnabled;
        this.selectedRecipients[row.id] = this.splitEmails(row.recipients);
        this.otherRecipients[row.id] = this.splitEmails(row.otherRecipients);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to update ${row.displayName}`
        });
      }
    });
  }

  toggleMaster(): void {
    const master = this.masterSwitch;
    if (!master) return;

    // The switch has already flipped in the model via ngModel; turning it OFF is the
    // destructive direction, so confirm that and revert if the admin backs out.
    const turningOff = !master.isEnabled;
    if (!turningOff) {
      this.saveMaster(true);
      return;
    }

    this.confirmationService.confirm({
      message: 'This stops ALL outbound email from the system — contract reminders, invoice reminders, work log notifications, onboarding and outing emails. Continue?',
      header: 'Disable all email notifications?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.saveMaster(false),
      reject: () => { master.isEnabled = true; }
    });
  }

  private saveMaster(enabled: boolean): void {
    this.notificationSettingService.setMaster(enabled)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updated) => {
          this.masterSwitch = updated;
          this.messageService.add({
            severity: enabled ? 'success' : 'warn',
            summary: enabled ? 'Email enabled' : 'All email disabled',
            detail: enabled
              ? 'Notifications will be sent according to the settings below.'
              : 'No emails will be sent until this is switched back on.'
          });
        },
        error: (error) => {
          console.error('Error updating master switch:', error);
          if (this.masterSwitch) {
            this.masterSwitch.isEnabled = !enabled;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update the master switch'
          });
        }
      });
  }

  isSaving(row: NotificationSetting): boolean {
    return this.savingIds.has(row.id);
  }

  private splitEmails(raw?: string): string[] {
    if (!raw?.trim()) return [];
    return raw.split(/[;,]/).map(e => e.trim()).filter(e => e.length > 0);
  }
}
